/* eslint-disable @typescript-eslint/no-explicit-any */
import { camelize } from '../helpers/camelize';
import { serializeParams } from '../helpers/serialize-params';
import { isLeft } from 'fp-ts/lib/Either';
import { Type, Validation } from 'io-ts';

export type RequestBody = BodyInit | Record<string, unknown>;

export type ValueOrPromise<T> = T | Promise<T>;

/** UrlSearchParams defines the valid type for a URL search parameters */
type UrlSearchParams = Record<string, unknown> | string;

/** ParamsSerializer defines the type for a search parameters serializer */
type ParamsSerializer = (p: UrlSearchParams) => string;

/**
 * RequestOptions extends the RequestInit interface by adding fields required
 * for the RestDataSource class
 */
export type RequestOptions = RequestInit & {
  /* The request path */
  path?: string;
  /** Parameters for the Query String */
  params?: UrlSearchParams;
  /** The serializer function for the Query String parameters */
  paramsSerializer?: ParamsSerializer;
  /** Override Base URL */
  url?: string;
};

/**
 * RequestOptionsWithValidator exetends RequestOptions by adding a validator
 * field
 */
export interface RequestOptionsWithValidator<TResult> extends RequestOptions {
  /** Disable retry for a single request only */
  disableRequestRetry?: boolean;
  /** The type of the coded validator used to validate the response data */
  validator?: Type<TResult>;
}

/**
 * RequestResponse is the success response interface
 */
export interface RequestResponse<TResult> {
  /** The Fetch API Response interface */
  response: Response;
  /** The response data type */
  data: TResult;
}

/**
 * RequestError is the interface used for the data returned as a result of an
 * errored request.
 */
export interface RequestError {
  /** Request URL */
  url: string;
  /** Request Response status */
  status: number;
  /** Request response status text */
  statusText: string;
  /**  */
  message?: string;
  body?: Record<string, unknown> | string | null;
}

/**
 * RetrySettings defines the interface for the retry options used in the
 * RestDataSource class.
 */
export interface RetrySettings {
  /** Retry interval factor in ms */
  retryIntervalFactor: number;
  /** Callback function which returns true when a given response should
   *  trigger a retry
   */
  retryOnHTTPResponse: (res: Response) => boolean;
  /** The max number of retries to be made */
  retryMaxCount: number;
  /** retryTimeout */
  retryTimeout: number;
}

/**
 * Reporter defines an error reporter for schema validation
 */
export interface Reporter {
  report: (validation: Validation<unknown>, data?: unknown) => string[];
}

/**
 * RestDataSourceOptions defines the options consumers are allowed to pass to
 * the class constructor.
 */
export interface RestDataSourceOptions<TParams> {
  /** The specific set of parameters for each client */
  params?: TParams;
  /** Retry mechanism settings */
  retrySettings?: Partial<RetrySettings>;
  /** Disable retry mechanism */
  disableRetry?: boolean;
  /** If true, relevant information will be logged at runtime */
  debug?: boolean;
  /** An error reporter for response data validation */
  reporter?: Reporter;
}

/**
 * RestDataSource is a class implementation for a HTTP client. It provides
 * a complete solution to handling all request methods (GET, POST, PUT, etc).
 * It handles network and custom errors retry by applying an exponenatial back
 * of algorithm. This class allow for the clients to apply it's own set of
 * parameters to be used internally. Such as passing authentication parameters
 * which can be used in the 'willSendRequest' middleware to set headers.
 */
export class RestDataSource<TParams> {
  public baseURL?: string;
  public readonly params?: TParams;
  private disableRetry?: boolean;
  private reporter?: Reporter;

  protected retrySettings: RetrySettings = {
    retryIntervalFactor: 500,
    retryOnHTTPResponse: res => res.status >= 500,
    retryMaxCount: Number.MAX_SAFE_INTEGER,
    retryTimeout: 64000, // 64s
  };
  private debug = false;

  constructor({
    params,
    retrySettings,
    debug,
    disableRetry,
    reporter,
  }: RestDataSourceOptions<TParams>) {
    this.params = params;

    if (retrySettings) {
      // merge the provided retry settings with the default values
      this.retrySettings = {
        ...this.retrySettings,
        ...retrySettings,
      };
    }

    if (debug) {
      this.debug = true;
    }

    this.reporter = reporter;

    this.disableRetry = Boolean(disableRetry);
  }

  public setDisableRetry(value: boolean) {
    this.disableRetry = value;
  }

  /**
   * willSendRequest is a middleware execute imeddiately before a request is
   * sent. It is usefull for setting headers for instance
   */
  protected willSendRequest?(request: RequestOptions): ValueOrPromise<void>;

  /**
   * didReceiveResponse handles a successful response
   */
  protected async didReceiveResponse<TResult = any>(
    response: Response,
    validator?: Type<TResult>,
    // _request: Request,
  ): Promise<RequestResponse<TResult>> {
    if (!response.ok) {
      throw await this.errorFromResponse(response);
    }

    try {
      const data = ((await this.parseBody<TResult>(
        response,
        validator,
      )) as unknown) as TResult;
      return {
        data,
        response,
      };
    } catch (e) {
      throw this.errorParsingResponseData(response, e);
    }
  }

  /**
   * parseBody parses and validates the response body when a 'validator' is
   * provided
   */
  protected async parseBody<TResult>(
    response: Response,
    validator?: Type<TResult>,
  ): Promise<Record<string, unknown> | string> {
    // For now parse everything to JSON because the APIs aren't returning a
    // consitent 'Content-type' header return camelize(await response.json())

    const contentType = response.headers.get('Content-Type');
    const contentLength = response.headers.get('Content-Length');
    if (
      // As one might expect, a "204 No Content" is empty! This means there
      // isn't enough to `JSON.parse`, and trying will result in an error.
      response.status !== 204 &&
      contentLength !== '0' &&
      contentType &&
      (contentType.startsWith('application/json') ||
        contentType.startsWith('application/hal+json'))
    ) {
      const json = camelize(await response.json());
      if (validator) {
        const result = validator.decode(json);

        if (isLeft(result) && this.reporter) {
          const report = this.reporter.report(result, json);
          const errorMsg = report.join('\n');

          throw errorMsg;
        }
      }

      return json;
    }
    return response.text();
  }

  /**
   * errorParsingResponseData generates the error output when there is an error
   * parsing the response data
   */
  protected errorParsingResponseData(
    response: Response,
    errorMsg: string,
  ): RequestError {
    return {
      url: response.url,
      status: response.status,
      statusText: response.statusText,
      message: errorMsg,
    };
  }

  /**
   * errorFromResponse  generates the error output when there is a general
   * response error
   */
  protected async errorFromResponse(response: Response): Promise<RequestError> {
    const message = `${response.status}: ${response.statusText}`;
    let bodyParsed = null;
    try {
      bodyParsed = await this.parseBody(response);
    } catch {
      if (process && process.env && process.env.NODE_ENV === 'development') {
        console.log('Cannot parse body from errored response!');
      }
    }

    return {
      url: response.url,
      status: response.status,
      statusText: response.statusText,
      message,
      body: bodyParsed,
    };
  }

  /**
   * resolveURL resolves the final request URL by serializing the parameters
   * and constructing the final URL string
   */
  protected resolveURL({
    path,
    params,
    paramsSerializer,
    url: tempUrl,
  }: RequestOptions): string {
    const rgxA = /\/$/g; // matches a '/' at the end of the string
    const rgxB = /^\//g; // matches a '/' at the begining of the string

    let url = tempUrl || this.baseURL || '';
    const formattedURL = url.replace(rgxA, '');
    const formattedPath = path ? path.replace(rgxB, '') : '';

    url = path ? `${formattedURL}/${formattedPath}` : url;

    if (!params) {
      return url;
    }

    const serializedParams =
      typeof params === 'string'
        ? params
        : paramsSerializer
        ? paramsSerializer(params)
        : serializeParams(params);

    if (serializedParams) {
      const hashmarkIndex = url.indexOf('#');
      if (hashmarkIndex !== -1) {
        url = url.slice(0, hashmarkIndex);
      }

      url += (url.indexOf('?') === -1 ? '?' : '&') + serializedParams;
    }

    return url;
  }

  /**
   * get performs a GET request
   */
  protected async get<TResult = any>(
    path: string,
    params?: UrlSearchParams,
    init?: RequestOptionsWithValidator<TResult>,
  ): Promise<RequestResponse<TResult>> {
    const { paramsSerializer, validator, ...initRest } = init || {};
    return this.fetch<TResult>(
      Object.assign(
        { method: 'GET', path, params, paramsSerializer, validator },
        initRest,
      ),
    );
  }

  /**
   * post performs a POST request
   */
  protected async post<TResult = any>(
    path: string,
    body?: RequestBody,
    init?: RequestOptionsWithValidator<TResult>,
  ): Promise<RequestResponse<TResult>> {
    return this.fetch<TResult>(
      Object.assign({ method: 'POST', path, body }, init),
    );
  }

  /**
   * patch performs a PATCH request
   */
  protected async patch<TResult = any>(
    path: string,
    body?: RequestBody,
    init?: RequestOptionsWithValidator<TResult>,
  ): Promise<RequestResponse<TResult>> {
    return this.fetch<TResult>(
      Object.assign({ method: 'PATCH', path, body }, init),
    );
  }

  /**
   * put performs a PUT request
   */
  protected async put<TResult = any>(
    path: string,
    body?: RequestBody,
    init?: RequestOptionsWithValidator<TResult>,
  ): Promise<RequestResponse<TResult>> {
    return this.fetch<TResult>(
      Object.assign({ method: 'PUT', path, body }, init),
    );
  }

  /**
   * delete performs a DELETE request
   */
  protected async delete<TResult = any>(
    path: string,
    params?: UrlSearchParams,
    init?: RequestOptionsWithValidator<TResult>,
  ): Promise<RequestResponse<TResult>> {
    const { paramsSerializer, ...initRest } = init || {};
    return this.fetch<TResult>(
      Object.assign(
        { method: 'DELETE', path, params, paramsSerializer },
        initRest,
      ),
    );
  }

  /**
   * fetch performs the fetching, retrying and forwarding error and success
   * responses to its respectively handler functions
   */
  private async fetch<TResult>(
    options: RequestOptionsWithValidator<TResult> & { path: string },
  ): Promise<RequestResponse<TResult>> {
    options.paramsSerializer =
      options.paramsSerializer ?? (serializeParams as ParamsSerializer);

    // Desconstruct the 'validator' out of options so the remaining object
    // complies with the RequestOptions interface
    const { validator, ...restOptions } = options;

    // Trigger 'willSendRequest' middleware
    if (this.willSendRequest) {
      await this.willSendRequest(restOptions);
    }

    const url = await this.resolveURL(restOptions);

    // Serialize body and set 'Content-Type' header when the body is a JSON
    if (
      restOptions.body !== undefined &&
      restOptions.body !== null &&
      (restOptions.body.constructor === Object ||
        Array.isArray(restOptions.body) ||
        ((restOptions.body as any).toJSON &&
          typeof (restOptions.body as any).toJSON === 'function'))
    ) {
      restOptions.body = JSON.stringify(restOptions.body);

      restOptions.headers = {
        ...(restOptions.headers || {}),
        'Content-Type': 'application/json',
      };
    }

    // Desconstruct overload variables out of 'opts' so the reamining object
    // 'restOptions' complies with the 'RequestInit' object
    const {
      path: _path,
      params: _params,
      paramsSerializer: _ps,
      ...opts
    } = restOptions;

    // Define a traceLabel for this request
    const traceLabel = `${options.method || 'GET'} ${url}`;

    // Track the starting time
    const startTime = Date.now();

    /**
     * doRequest performs a request and in case of fail it will trigger a
     * recursive retrying based on the 'retrySettings' defined for the client
     * @param retryCount
     */
    const doRequest = async (retryCount = 1): Promise<Response> => {
      try {
        const response = await fetch(url, opts);

        // Track the duration of each request and log it at the 'debug' level
        const duration = Date.now() - startTime;
        this.logDebug(`${traceLabel} (${duration}ms)`);

        if (
          !options.disableRequestRetry &&
          this.retrySettings.retryOnHTTPResponse(response)
        ) {
          // Trigger the retry mechanism based on the 'retryOnHTTPResponse'
          throw response.statusText;
        }

        // The request was successfull so it passes the response forward
        return response;
      } catch (error) {
        const duration = Date.now() - startTime;
        if (
          this.disableRetry ||
          options.disableRequestRetry ||
          retryCount > this.retrySettings.retryMaxCount ||
          duration > this.retrySettings.retryTimeout
        ) {
          // Exceeded the number of retries or the timeout so it gives up and
          // forward the error
          throw error;
        }

        // Calculate and print the retrying message on the debug level
        const delayT = this.caclulateDelay(retryCount);
        this.logInfo(
          `Request for ${url} failed. Retry count: ${retryCount} Retrying in (${delayT}ms)`,
          error,
        );

        // Wait for the delay and trigger another retry
        await this.delay(retryCount);
        return await doRequest(++retryCount);
      }
    };

    const res = await doRequest();

    return await this.didReceiveResponse(res, validator);
  }

  private logDebug(msg: string, ...params: any[]) {
    if (this.debug) {
      console.debug(msg, ...params);
    }
  }

  private logInfo(msg: string, ...params: any[]) {
    if (process.env.NODE_ENV !== 'test') {
      console.info(msg, ...params);
    }
  }

  private caclulateDelay(retryCount: number) {
    return 2 ** retryCount * this.retrySettings.retryIntervalFactor;
  }

  /**
   * delay triggers an async function used to create intervals between fetch
   * retries
   * @param retryCount
   */
  private async delay(retryCount: number) {
    return new Promise(resolve =>
      setTimeout(resolve, this.caclulateDelay(retryCount)),
    );
  }
}
